/**
 * Users and Roles — one row per Email key (ADR-0022).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import logger from '@/lib/logging';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  UserPlus,
  Edit,
  Trash2,
  Search,
  Mail,
  ChevronLeft,
  ChevronRight,
  Link2,
  Loader2,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import {
  requestAdminCopySignInLink,
  requestAdminSendSignInEmail,
  fetchLastSignInLinkSent,
} from '@/lib/sign-in-link-api';
import { copyTextFromPromise } from '@/lib/copy-to-clipboard';
import {
  isSignInLinkCopySourceError,
  SignInLinkCopySourceError,
} from '@/lib/sign-in-link-copy-errors';
import { assignApiRoleDetailed, removeApiRole } from '@/lib/role-operations-api';
import { UserRole, ROLE_METADATA, CORE_ROLES } from '@/components/authnz/types/rbac';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkActionsToolbar } from './BulkActionsToolbar';
import { TypedEmailConfirmation } from '@/components/authnz/TypedEmailConfirmation';
import { isSelfTarget, typedEmailMatches } from '@/lib/admin-lockout-guards';
import { isStepUpRequiredFailure } from '@/lib/access/step-up';
import type { EmailKeyUserRow, ProblemFilter } from '@/lib/users-roles/types';
import {
  includeDeletedForProblemFilter,
  matchesProblemFilter,
  rowNeedsMigration,
} from '@/lib/users-roles/problem-filter';
import {
  USERS_ROLES_SORT_COLUMNS,
  nextUsersRolesSort,
  sortUsersRolesRows,
  type UsersRolesSortColumn,
  type UsersRolesSortDirection,
} from '@/lib/users-roles/sort-rows';
import {
  fetchAllUsers,
  fetchExplainAccess,
  migrateEmailKeyApi,
  restoreEmailKeyApi,
} from '@/components/authnz/services/users-api';
import { updateDisplayName } from '@/components/authnz/lib/user-management';
import type { EnhancedUser } from '@/components/authnz/lib/user-management';
import { GrantChips } from './users-roles/GrantChips';
import { RoleSwatch } from './users-roles/RoleSwatch';
import { LifecycleBadge } from './users-roles/LifecycleBadge';
import { UsersRolesMobileCard } from './users-roles/UsersRolesMobileCard';
import { ExplainAccessPanel } from './users-roles/ExplainAccessPanel';
import { StepUpAlertDialog } from './users-roles/StepUpAlertDialog';
import { emailKeyRowToExplainInput } from '@/lib/users-roles/to-explain-input';

export function toBulkUser(row: EmailKeyUserRow): EnhancedUser {
  return {
    userId: row.emailKey,
    email: row.emailKey,
    displayName: row.displayName,
    photoURL: row.photoURL,
    isGuest: false,
    roles: row.roles,
    primaryRole: row.primaryRole,
    accessStatus: null,
    banned: false,
    deleted: row.deleted,
  };
}

export function splitInviteEmails(raw: string): string[] {
  return [...new Set(raw.split(/[,\t;\n\s]+/).map((email) => email.trim()).filter(Boolean))];
}

export function UsersAndRolesTab() {
  const { user: currentUser, loading: authLoading, refreshServerSession } = useAuth();
  const { toast } = useToast();

  const [users, setUsers] = useState<EmailKeyUserRow[]>([]);
  const [fallbackHits, setFallbackHits] = useState(0);
  const listEtagRef = useRef<string | null>(null);
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<ProblemFilter>('all');
  const [sortColumn, setSortColumn] = useState<UsersRolesSortColumn>('emailKey');
  const [sortDirection, setSortDirection] = useState<UsersRolesSortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [assignRoleDrawerOpen, setAssignRoleDrawerOpen] = useState(false);
  const [assignEmailLocked, setAssignEmailLocked] = useState(false);
  const [assignTargetUid, setAssignTargetUid] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<UserRole>(UserRole.PRACTITIONER);
  const [isAssigning, setIsAssigning] = useState(false);
  const [migratingEmail, setMigratingEmail] = useState<string | null>(null);
  const [sendSignInEmailOnAssign, setSendSignInEmailOnAssign] = useState(true);

  const [editUserDrawerOpen, setEditUserDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<EmailKeyUserRow | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editSelfConfirmEmail, setEditSelfConfirmEmail] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState<EmailKeyUserRow | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState<EmailKeyUserRow | null>(null);
  const [assignDeletedConfirm, setAssignDeletedConfirm] = useState<{
    emailKey: string;
    role: UserRole;
  } | null>(null);
  const [roleRemoveConfirm, setRoleRemoveConfirm] = useState<{
    row: EmailKeyUserRow;
    role: UserRole;
  } | null>(null);
  const [typedEmail, setTypedEmail] = useState('');
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [copyingEmail, setCopyingEmail] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [lastLinkSentByEmail, setLastLinkSentByEmail] = useState<Record<string, string | null>>({});

  const includeDeleted = includeDeletedForProblemFilter(statusFilter);

  const bulkUsers = useMemo(() => users.map(toBulkUser), [users]);

  const loadUsers = useCallback(
    async (force = false) => {
      try {
        const result = await fetchAllUsers({
          includeDeleted,
          force,
          etag: force ? null : listEtagRef.current,
        });
        if (!result.notModified) {
          setUsers(result.data);
          setFallbackHits(result.fallbackHits);
          listEtagRef.current = result.etag;
          setSelectedUsers((prev) => {
            const valid = new Set(result.data.map((row) => row.emailKey));
            const next = new Set([...prev].filter((emailKey) => valid.has(emailKey)));
            return next.size === prev.size ? prev : next;
          });
        }
      } catch (error) {
        logger.error('Error loading users:', error);
        toastRef.current({
          title: 'Could not load users',
          description: error instanceof Error ? error.message : String(error),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [includeDeleted],
  );

  useEffect(() => {
    if (authLoading || !currentUser?.uid) {
      return;
    }
    setLoading(true);
    void loadUsers(true);
  }, [authLoading, currentUser?.uid, includeDeleted, loadUsers]);

  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (row) =>
          row.emailKey.includes(q) ||
          row.displayName.toLowerCase().includes(q) ||
          (row.uid ?? '').toLowerCase().includes(q),
      );
    }
    if (roleFilter !== 'all') {
      filtered = filtered.filter((row) => row.roles.includes(roleFilter as UserRole));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((row) => matchesProblemFilter(row, statusFilter));
    }
    return sortUsersRolesRows(filtered, sortColumn, sortDirection);
  }, [users, searchQuery, roleFilter, statusFilter, sortColumn, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, statusFilter, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + pageSize);
  const selectedOnPage = paginatedUsers.filter((row) => selectedUsers.has(row.emailKey)).length;
  const allPageSelected = paginatedUsers.length > 0 && selectedOnPage === paginatedUsers.length;
  const somePageSelected = selectedOnPage > 0 && selectedOnPage < paginatedUsers.length;
  const offPageSelectedCount = [...selectedUsers].filter(
    (emailKey) => !paginatedUsers.some((row) => row.emailKey === emailKey),
  ).length;
  const allMatchingSelected =
    filteredUsers.length > 0 && filteredUsers.every((row) => selectedUsers.has(row.emailKey));

  const toggleSelected = (emailKey: string) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(emailKey)) {
        next.delete(emailKey);
      } else {
        next.add(emailKey);
      }
      return next;
    });
  };

  const selectPage = () => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      for (const row of paginatedUsers) {
        next.add(row.emailKey);
      }
      return next;
    });
  };

  const deselectPage = () => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      for (const row of paginatedUsers) {
        next.delete(row.emailKey);
      }
      return next;
    });
  };

  const selectMatching = () => {
    setSelectedUsers(new Set(filteredUsers.map((row) => row.emailKey)));
  };

  const toggleSort = (column: UsersRolesSortColumn) => {
    const next = nextUsersRolesSort(sortColumn, sortDirection, column);
    setSortColumn(next.column);
    setSortDirection(next.direction);
  };

  const ariaSortFor = (column: UsersRolesSortColumn): 'ascending' | 'descending' | 'none' =>
    sortColumn !== column ? 'none' : sortDirection === 'asc' ? 'ascending' : 'descending';

  const sortIconFor = (column: UsersRolesSortColumn) =>
    sortColumn !== column ? (
      <ArrowUpDown className="h-3 w-3 text-muted-foreground" aria-hidden />
    ) : sortDirection === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-muted-foreground" aria-hidden />
    ) : (
      <ArrowDown className="h-3 w-3 text-muted-foreground" aria-hidden />
    );

  const openStepUpIfNeeded = (error: unknown): boolean => {
    if (!isStepUpRequiredFailure(error)) {
      return false;
    }
    setDeleteConfirm(null);
    setStepUpOpen(true);
    return true;
  };

  const openHeaderInvite = () => {
    setAssignEmailLocked(false);
    setAssignTargetUid(null);
    setNewEmail('');
    setNewRole(UserRole.PRACTITIONER);
    setSendSignInEmailOnAssign(true);
    setAssignRoleDrawerOpen(true);
  };

  const openRowAssign = (row: EmailKeyUserRow) => {
    setAssignEmailLocked(true);
    setAssignTargetUid(row.uid);
    setNewEmail(row.emailKey);
    setNewRole(UserRole.PRACTITIONER);
    setSendSignInEmailOnAssign(false);
    setAssignRoleDrawerOpen(true);
  };

  const handleMigrate = async (row: EmailKeyUserRow) => {
    setMigratingEmail(row.emailKey);
    try {
      const result = await migrateEmailKeyApi(row.emailKey);
      if (!result.ok && result.status === 409 && result.code === 'offboarded') {
        setRestoreConfirm(row);
        toast({
          title: 'Restore first',
          description: 'Deleted emails must be restored before migration.',
        });
        return;
      }
      if (!result.ok) {
        toast({ title: 'Migrate failed', description: result.message, variant: 'destructive' });
        return;
      }
      toast({ title: 'Migrated', description: `Folded legacy roles for ${row.emailKey}.` });
      await refreshServerSession();
      await loadUsers(true);
    } finally {
      setMigratingEmail(null);
    }
  };

  const handleAssignRole = async () => {
    if (!currentUser || !newEmail.trim()) return;
    setIsAssigning(true);
    const emails = splitInviteEmails(newEmail);
    try {
      for (const email of emails) {
        const result = await assignApiRoleDetailed(
          assignEmailLocked && emails.length === 1 ? assignTargetUid : null,
          email,
          newRole,
          currentUser.uid,
        );
        if (
          !result.ok &&
          result.status === 409 &&
          (result.code === 'offboarded' || /deleted email until Restore/i.test(result.message))
        ) {
          setAssignDeletedConfirm({ emailKey: email.toLowerCase(), role: newRole });
          return;
        }
        if (!result.ok) {
          toast({ title: 'Assign failed', description: result.message, variant: 'destructive' });
          return;
        }
      }
      toast({ title: 'Roles assigned', description: `Assigned role to ${emails.length} email(s).` });
      if (sendSignInEmailOnAssign) {
        const sendResults = await Promise.all(
          emails.map(async (email) => ({
            email,
            result: await requestAdminSendSignInEmail(email),
          })),
        );
        notifySignInEmailSendResults(sendResults);
      }
      setAssignRoleDrawerOpen(false);
      setNewEmail('');
      await refreshServerSession();
      await loadUsers(true);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRestore = async (emailKey: string) => {
    try {
      await restoreEmailKeyApi(emailKey);
      toast({ title: 'Restored', description: `${emailKey} can receive grants again.` });
      setRestoreConfirm(null);
      setAssignDeletedConfirm(null);
      await refreshServerSession();
      await loadUsers(true);
    } catch (error) {
      toast({
        title: 'Restore failed',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      });
    }
  };

  const notifySignInEmailSendResults = (
    sendResults: Array<{ email: string; result: { success: boolean; error?: string; message?: string } }>,
  ) => {
    const failed = sendResults.filter((item) => !item.result.success);
    const stepUpFailure = failed.find(
      (item) =>
        isStepUpRequiredFailure(item.result.error) || isStepUpRequiredFailure(item.result.message),
    );
    if (stepUpFailure) {
      setStepUpOpen(true);
      return;
    }
    if (failed.length === 0) {
      toast({
        title: 'Sign-in email sent',
        description: `Sent sign-in email to ${sendResults.length} email(s).`,
      });
      return;
    }
    toast({
      title: 'Sign-in email not sent',
      description: failed
        .map((item) => item.result.message || `Could not send to ${item.email}.`)
        .join(' '),
      variant: 'destructive',
    });
  };

  const handleSendSignInEmail = async (email: string) => {
    if (!email || sendingEmail) return;
    setSendingEmail(email);
    try {
      const result = await requestAdminSendSignInEmail(email);
      notifySignInEmailSendResults([{ email, result }]);
    } finally {
      setSendingEmail(null);
    }
  };

  const handleCopySignInLink = async (email: string) => {
    if (!email || copyingEmail) return;
    setCopyingEmail(email);
    try {
      await copyTextFromPromise(async () => {
        const result = await requestAdminCopySignInLink(email);
        if (!result.success) {
          if (isStepUpRequiredFailure(result.error) || isStepUpRequiredFailure(result.message)) {
            setStepUpOpen(true);
            throw new Error(result.message);
          }
          throw new SignInLinkCopySourceError('api', result.message || 'Could not copy link.', {
            error: result.error,
          });
        }
        return result.link ?? '';
      });
      toast({ title: 'Sign-in link copied', description: 'Link copied to clipboard.' });
    } catch (error) {
      if (!openStepUpIfNeeded(error) && !isSignInLinkCopySourceError(error)) {
        toast({
          title: 'Copy failed',
          description: error instanceof Error ? error.message : String(error),
          variant: 'destructive',
        });
      }
    } finally {
      setCopyingEmail(null);
    }
  };

  const handleDeleteUser = async (target: EmailKeyUserRow) => {
    if (!currentUser) return;
    try {
      const { bulkDeleteUsersApi } = await import('@/lib/users-admin-api');
      const result = await bulkDeleteUsersApi(
        [target.emailKey],
        currentUser.uid,
        {
          confirmSelf: actingOnSelf(target),
        },
      );
      if (result.success > 0) {
        toast({ title: 'Deleted', description: target.emailKey });
        setDeleteConfirm(null);
        setTypedEmail('');
        await refreshServerSession();
        await loadUsers(true);
      } else {
        const err = result.errors[0]?.error ?? '';
        if (isStepUpRequiredFailure(err)) {
          setDeleteConfirm(null);
          setStepUpOpen(true);
        } else {
          toast({ title: 'Delete failed', description: err, variant: 'destructive' });
        }
      }
    } catch (error) {
      if (!openStepUpIfNeeded(error)) {
        toast({
          title: 'Delete failed',
          description: error instanceof Error ? error.message : String(error),
          variant: 'destructive',
        });
      }
    }
  };

  const actingOnSelf = (target: EmailKeyUserRow) =>
    isSelfTarget({
      actorUid: currentUser?.uid,
      actorEmail: currentUser?.email,
      targetUid: target.uid ?? undefined,
      targetEmail: target.emailKey,
    });

  const openEditDrawer = async (row: EmailKeyUserRow) => {
    setEditingUser(row);
    setEditDisplayName(row.displayName);
    setEditSelfConfirmEmail('');
    setEditUserDrawerOpen(true);
    void fetchExplainAccess(row.emailKey).catch(() => undefined);
  };

  const roleRemoveRequiresTypedSelf =
    Boolean(roleRemoveConfirm) &&
    actingOnSelf(roleRemoveConfirm!.row) &&
    roleRemoveConfirm?.role === UserRole.ADMINISTRATOR;

  const deleteRequiresTypedSelf = Boolean(deleteConfirm) && actingOnSelf(deleteConfirm!);

  return (
    <div className="space-y-6 min-w-0 max-w-full">
      <Card>
        <CardContent className="pt-4">
          <div
            className="flex flex-wrap items-center gap-4 text-sm"
            data-testid="role-legend"
          >
            <span className="text-muted-foreground font-medium">Roles:</span>
            {CORE_ROLES.map((role) => (
              <div key={role} className="flex items-center gap-2">
                <RoleSwatch role={role} />
                <span className="text-muted-foreground">{ROLE_METADATA[role].displayName}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center justify-between">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search email key..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-10"
              aria-label="Search email key"
              data-testid="users-roles-search"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger
              className="w-full md:w-40 h-10"
              aria-label="Role"
              data-testid="user-role-filter"
            >
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {CORE_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_METADATA[role].displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ProblemFilter)}>
            <SelectTrigger
              className="w-full md:w-52 h-10"
              aria-label="Lifecycle"
              data-testid="user-lifecycle-filter"
            >
              <SelectValue placeholder="Lifecycle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="existing">Existing</SelectItem>
              <SelectItem value="invited-not-signed-in">Invited, not signed in</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
              <SelectItem value="auth-disabled-without-deleted">Auth disabled</SelectItem>
              <SelectItem value="fallback">Legacy fallback</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void loadUsers(true)}
            className="h-10"
            data-testid="users-roles-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <Button
          onClick={openHeaderInvite}
          className="h-10"
          data-testid="header-invite-user"
          aria-label="Invite user"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Invite user
        </Button>
      </div>

      <Card data-testid="users-roles-table-card">
        <CardHeader>
          <BulkActionsToolbar
            selectedUsers={selectedUsers}
            users={bulkUsers}
            rows={users}
            matchingCount={filteredUsers.length}
            allMatchingSelected={allMatchingSelected}
            offPageSelectedCount={offPageSelectedCount}
            onSelectMatching={selectMatching}
            onOperationComplete={() => void loadUsers(true)}
            onClearSelection={() => setSelectedUsers(new Set())}
            onStepUpRequired={() => setStepUpOpen(true)}
          />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Users ({filteredUsers.length})</CardTitle>
              <CardDescription>
                One row per Email key. On-demand refresh with ETag.
                {fallbackHits > 0
                  ? ` ${fallbackHits} grant(s) still hit legacy role storage — run migration when ready.`
                  : ''}
              </CardDescription>
            </div>
            {filteredUsers.length > 0 ? (
              <Button
                variant="outline"
                className="h-10 shrink-0"
                data-testid="select-all-matching"
                disabled={allMatchingSelected}
                onClick={selectMatching}
              >
                Select all {filteredUsers.length} matching
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground py-8 text-center">Loading users...</p>
          ) : paginatedUsers.length === 0 ? (
            <div className="py-8 text-center space-y-3">
              <p className="text-muted-foreground">No users found</p>
              <Button onClick={openHeaderInvite} data-testid="empty-invite-user">
                Invite user
              </Button>
            </div>
          ) : (
            <>
              <div
                className="md:hidden space-y-3"
                data-testid="users-roles-mobile-list"
              >
                {paginatedUsers.map((row) => (
                  <UsersRolesMobileCard
                    key={row.emailKey}
                    row={row}
                    selected={selectedUsers.has(row.emailKey)}
                    onToggleSelected={toggleSelected}
                    copyingEmail={copyingEmail}
                    sendingEmail={sendingEmail}
                    migratingEmail={migratingEmail}
                    onManage={(target) => void openEditDrawer(target)}
                    onCopyLink={(emailKey) => void handleCopySignInLink(emailKey)}
                    onSendEmail={(emailKey) => void handleSendSignInEmail(emailKey)}
                    onRestore={setRestoreConfirm}
                    onMigrate={(target) => void handleMigrate(target)}
                    onRevokeAdmin={(role) => setRoleRemoveConfirm({ row, role })}
                  />
                ))}
              </div>
              <div
                className="hidden md:block overflow-x-auto touch-pan-xy"
                data-testid="users-roles-desktop-table"
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          aria-label="Select this page"
                          data-testid="select-page-checkbox"
                          checked={somePageSelected ? 'indeterminate' : allPageSelected}
                          onCheckedChange={(value) => {
                            if (value === true) {
                              selectPage();
                            } else {
                              deselectPage();
                            }
                          }}
                        />
                      </TableHead>
                      {USERS_ROLES_SORT_COLUMNS.map((column) => {
                        const labels: Record<UsersRolesSortColumn, string> = {
                          emailKey: 'Email key',
                          displayName: 'Display name',
                          grants: 'Grants',
                          lifecycle: 'Lifecycle',
                        };
                        return (
                          <TableHead key={column} aria-sort={ariaSortFor(column)}>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 hover:text-foreground"
                              data-testid={`sort-${column}`}
                              aria-label={`Sort by ${labels[column]}`}
                              onClick={() => toggleSort(column)}
                            >
                              {labels[column]}
                              {sortIconFor(column)}
                            </button>
                          </TableHead>
                        );
                      })}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUsers.map((row) => (
                      <TableRow key={row.emailKey} data-state={selectedUsers.has(row.emailKey) ? 'selected' : undefined}>
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${row.emailKey}`}
                            data-testid={`select-user-${row.emailKey}`}
                            checked={selectedUsers.has(row.emailKey)}
                            onCheckedChange={() => toggleSelected(row.emailKey)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{row.emailKey}</TableCell>
                        <TableCell>{row.displayName || '—'}</TableCell>
                        <TableCell>
                          <GrantChips
                            grants={row.grants}
                            onRevokeAdmin={(role) => setRoleRemoveConfirm({ row, role })}
                            revokeAlwaysVisible
                          />
                        </TableCell>
                        <TableCell>
                          <LifecycleBadge row={row} />
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {row.invitedNotSignedIn && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid="copy-sign-in-link"
                                onClick={() => void handleCopySignInLink(row.emailKey)}
                                disabled={copyingEmail === row.emailKey}
                              >
                                <Link2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid="send-sign-in-email"
                                aria-label={`Send sign-in email to ${row.emailKey}`}
                                onClick={() => void handleSendSignInEmail(row.emailKey)}
                                disabled={sendingEmail === row.emailKey}
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {!row.deleted && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`assign-role-${row.emailKey}`}
                              aria-label={`Assign role to ${row.emailKey}`}
                              onClick={() => openRowAssign(row)}
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          )}
                          {rowNeedsMigration(row) && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`migrate-user-${row.emailKey}`}
                              aria-label={`Migrate ${row.emailKey}`}
                              disabled={migratingEmail === row.emailKey}
                              onClick={() => void handleMigrate(row)}
                            >
                              {migratingEmail === row.emailKey ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Migrate'
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`edit-user-${row.emailKey}`}
                            onClick={() => void openEditDrawer(row)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {row.deleted ? (
                            <Button
                              size="sm"
                              onClick={() => setRestoreConfirm(row)}
                              data-testid="restore-user"
                            >
                              Restore
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() => {
                                setTypedEmail('');
                                setDeleteConfirm(row);
                              }}
                              data-testid="delete-user"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
        {filteredUsers.length > 0 && (
          <div className="border-t px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="users-roles-page-size" className="text-sm">
                Users per page
              </Label>
              <Select
                value={pageSize === 10000 ? 'all' : pageSize.toString()}
                onValueChange={(value) => {
                  setPageSize(value === 'all' ? 10000 : Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger
                  id="users-roles-page-size"
                  className="h-10 w-20"
                  data-testid="users-roles-page-size"
                  aria-label="Users per page"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-2 sm:justify-end">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="h-10"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="h-10"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Sheet
        open={assignRoleDrawerOpen}
        onOpenChange={(open) => {
          setAssignRoleDrawerOpen(open);
          if (!open) {
            setAssignEmailLocked(false);
          }
        }}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{assignEmailLocked ? `Assign role to ${newEmail}` : 'Invite user'}</SheetTitle>
            <SheetDescription>
              {assignEmailLocked
                ? 'Adds a grant on this Email key through the Access API. Deleted emails require Restore first.'
                : 'Grant access to a new Email key that is not in the list yet. Deleted emails require Restore first.'}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            {assignEmailLocked ? (
              <p className="text-sm font-medium" data-testid="assign-target-email">
                {newEmail}
              </p>
            ) : (
              <Textarea
                value={newEmail}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewEmail(value);
                  if (splitInviteEmails(value).length > 1) {
                    setSendSignInEmailOnAssign(false);
                  }
                }}
                placeholder="email@example.com"
              />
            )}
            <Select value={newRole} onValueChange={(v) => setNewRole(v as UserRole)}>
              <SelectTrigger data-testid="assign-role-select" aria-label="Role to assign">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CORE_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {ROLE_METADATA[role].displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Checkbox
                id="send-sign-in-email"
                data-testid="send-sign-in-email-on-assign"
                checked={sendSignInEmailOnAssign}
                onCheckedChange={(c) => setSendSignInEmailOnAssign(c === true)}
              />
              <Label htmlFor="send-sign-in-email">Send sign-in email now</Label>
            </div>
          </div>
          <SheetFooter>
            <Button
              onClick={() => void handleAssignRole()}
              disabled={isAssigning || !newEmail.trim()}
              data-testid={assignEmailLocked ? 'assign-role-submit' : 'invite-user-submit'}
            >
              {assignEmailLocked
                ? isAssigning
                  ? 'Assigning…'
                  : 'Assign'
                : isAssigning
                  ? 'Inviting…'
                  : 'Invite'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={editUserDrawerOpen} onOpenChange={setEditUserDrawerOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit {editingUser?.emailKey}</SheetTitle>
          </SheetHeader>
          {editingUser && (
            <div className="space-y-4 py-4 min-w-0">
              <LifecycleBadge row={editingUser} />
              <div className="space-y-2 min-w-0">
                <Label>Grants</Label>
                <GrantChips
                  grants={editingUser.grants}
                  onRevokeAdmin={(role) => setRoleRemoveConfirm({ row: editingUser, role })}
                  revokeAlwaysVisible
                />
              </div>
              {!editingUser.deleted && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-full"
                  data-testid={`sheet-assign-role-${editingUser.emailKey}`}
                  aria-label={`Assign role to ${editingUser.emailKey}`}
                  onClick={() => {
                    setEditUserDrawerOpen(false);
                    openRowAssign(editingUser);
                  }}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign role
                </Button>
              )}
              <div className="space-y-2">
                <Label htmlFor="display-name">Display name</Label>
                <Input
                  id="display-name"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                />
              </div>
              <ExplainAccessPanel diagnostic={emailKeyRowToExplainInput(editingUser)} />
              {editingUser.deleted ? (
                <Button
                  type="button"
                  className="h-11 w-full"
                  data-testid="sheet-restore-user"
                  onClick={() => setRestoreConfirm(editingUser)}
                >
                  Restore
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-11 w-full text-destructive"
                  data-testid="sheet-delete-user"
                  onClick={() => {
                    setTypedEmail('');
                    setDeleteConfirm(editingUser);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          )}
          <SheetFooter>
            <Button
              className="h-11"
              data-testid="save-display-name"
              onClick={async () => {
                if (!editingUser) return;
                const saved = await updateDisplayName(editingUser.operationUserId, editDisplayName);
                if (!saved) {
                  toast({
                    title: 'Could not save display name',
                    description: editingUser.emailKey,
                    variant: 'destructive',
                  });
                  return;
                }
                toast({ title: 'Display name saved', description: editingUser.emailKey });
                setEditUserDrawerOpen(false);
                await loadUsers(true);
              }}
            >
              Save display name
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {assignDeletedConfirm && (
        <AlertDialog open onOpenChange={() => setAssignDeletedConfirm(null)}>
          <AlertDialogContent data-testid="assign-deleted-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Email is deleted</AlertDialogTitle>
              <AlertDialogDescription>
                {assignDeletedConfirm.emailKey} is deleted (409). Restore before assigning roles.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  void handleRestore(assignDeletedConfirm.emailKey);
                }}
              >
                Restore
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {deleteConfirm && (
        <AlertDialog open onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent data-testid="delete-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {deleteConfirm.emailKey}?</AlertDialogTitle>
              <AlertDialogDescription>Requires recent sign-in (step-up).</AlertDialogDescription>
            </AlertDialogHeader>
            {deleteRequiresTypedSelf && (
              <TypedEmailConfirmation
                email={deleteConfirm.emailKey}
                value={typedEmail}
                onChange={setTypedEmail}
              />
            )}
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={deleteRequiresTypedSelf && !typedEmailMatches(typedEmail, deleteConfirm.emailKey)}
                onClick={(event) => {
                  event.preventDefault();
                  void handleDeleteUser(deleteConfirm);
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {restoreConfirm && (
        <AlertDialog open onOpenChange={() => setRestoreConfirm(null)}>
          <AlertDialogContent data-testid="restore-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Restore {restoreConfirm.emailKey}?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  void handleRestore(restoreConfirm.emailKey);
                }}
              >
                Restore
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {roleRemoveConfirm && (
        <AlertDialog open onOpenChange={() => setRoleRemoveConfirm(null)}>
          <AlertDialogContent data-testid="remove-role-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove role?</AlertDialogTitle>
            </AlertDialogHeader>
            {roleRemoveRequiresTypedSelf && (
              <TypedEmailConfirmation
                email={roleRemoveConfirm.row.emailKey}
                value={typedEmail}
                onChange={setTypedEmail}
              />
            )}
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={
                  roleRemoveRequiresTypedSelf &&
                  !typedEmailMatches(typedEmail, roleRemoveConfirm.row.emailKey)
                }
                onClick={(event) => {
                  event.preventDefault();
                  const target = roleRemoveConfirm;
                  const confirmSelf = roleRemoveRequiresTypedSelf;
                  void (async () => {
                    try {
                      await removeApiRole(
                        target.row.uid ?? target.row.operationUserId,
                        target.role,
                        target.row.emailKey,
                        { confirmSelf },
                      );
                      toast({
                        title: 'Role removed',
                        description: `${ROLE_METADATA[target.role].displayName} removed from ${target.row.emailKey}.`,
                      });
                      setRoleRemoveConfirm(null);
                      await refreshServerSession();
                      await loadUsers(true);
                    } catch (error) {
                      if (!openStepUpIfNeeded(error)) {
                        toast({
                          title: 'Remove failed',
                          description: error instanceof Error ? error.message : String(error),
                          variant: 'destructive',
                        });
                      }
                    }
                  })();
                }}
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <StepUpAlertDialog open={stepUpOpen} onOpenChange={setStepUpOpen} />
    </div>
  );
}
